import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { jobService } from '../../services/jobService';
import { Job, JobFilters } from '../../types/job';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface JobListProps {
  filters: JobFilters;
  onView: (job: Job) => void;
  onEdit: (job: Job) => void;
}

const statusStyle: Record<string, React.CSSProperties> = {
  ACTIVE:    { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  COMPLETED: { background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' },
  ON_HOLD:   { background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' },
  CANCELLED: { background: 'rgba(80,80,80,0.15)', color: '#555570', border: '1px solid rgba(80,80,80,0.2)' },
};

const statusLabel: Record<string, string> = {
  ACTIVE: 'Active', COMPLETED: 'Completed', ON_HOLD: 'On Hold', CANCELLED: 'Cancelled',
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const JobList: React.FC<JobListProps> = ({ filters, onView, onEdit }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['jobs', filters],
    queryFn: () => jobService.getJobs(filters)
  });

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Jobs</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 36, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Error loading jobs. Please try again.</p>
      </div>
    );
  }

  if (!data?.data.length) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
        <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO JOBS FOUND</p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 6 }}>Get started by creating your first job.</p>
      </div>
    );
  }

  const getProgressPercentage = (job: Job) => {
    if (job.status === 'COMPLETED') return 100;
    if (job.status === 'CANCELLED') return 0;
    if (!job.estimatedValue || job.estimatedValue === 0) return 0;
    return Math.min(100, (job.actualValue / job.estimatedValue) * 100);
  };

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Jobs</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            {data.pagination.totalItems} total jobs
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
              <th style={thStyle}>Job Details</th>
              <th style={thStyle}>Customer</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Progress</th>
              <th style={thStyle}>Value</th>
              <th style={thStyle}>Bills / Invoices</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((job) => {
              const progress = getProgressPercentage(job);
              return (
                <tr
                  key={job.id}
                  style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={tdStyle}>
                    <div style={{ color: '#1c2033', fontWeight: 500 }}>{job.jobNumber}</div>
                    <div style={{ fontWeight: 600, color: '#5b5a52', marginTop: 2 }}>{job.jobName}</div>
                    <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>Created: {formatDate(job.createdAt)}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ color: '#5b5a52' }}>{job.customer?.name}</div>
                    {job.customer?.email && (
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{job.customer.email}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      ...(statusStyle[job.status] || statusStyle.CANCELLED),
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 3,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                    }}>
                      {statusLabel[job.status] || job.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 4, background: 'rgba(92,78,51,0.2)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{
                          width: `${progress}%`,
                          height: '100%',
                          background: progress === 100
                            ? 'linear-gradient(90deg, #5ac98c, #3ab87a)'
                            : 'linear-gradient(90deg, #d97706, #b45309)',
                          borderRadius: 2,
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
                        {progress.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {job.estimatedValue ? (
                      <div style={{ fontSize: 11, color: '#6b6a5c' }}>
                        Est: <span style={{ color: '#5b5a52' }}>{formatCurrency(job.estimatedValue)}</span>
                      </div>
                    ) : null}
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#fbbf24', marginTop: job.estimatedValue ? 2 : 0 }}>
                      {formatCurrency(job.actualValue)}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontSize: 11, color: '#5a8cc9' }}>
                        {job._count?.bills || 0} Bills
                      </span>
                      <span style={{ fontSize: 11, color: '#5ac98c' }}>
                        {job._count?.invoices || 0} Invoices
                      </span>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                      <button
                        onClick={() => onView(job)}
                        style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => onEdit(job)}
                        style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pagination.totalPages > 1 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
            Showing {((data.pagination.currentPage - 1) * data.pagination.itemsPerPage) + 1}–{Math.min(data.pagination.currentPage * data.pagination.itemsPerPage, data.pagination.totalItems)} of {data.pagination.totalItems}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[...Array(data.pagination.totalPages)].map((_, i) => {
              const pageNum = i + 1;
              const isCurrentPage = pageNum === data.pagination.currentPage;
              return (
                <button
                  key={i}
                  style={{
                    padding: '4px 10px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    background: isCurrentPage ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(239,232,213,0.5)',
                    color: isCurrentPage ? '#1c2033' : '#8a8778',
                    border: isCurrentPage ? 'none' : '1px solid rgba(92,78,51,0.22)',
                    borderRadius: 3,
                    cursor: 'pointer',
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
